import { CommonModule, Location } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  ElementRef,
  HostListener,
  inject,
  OnDestroy,
  OnInit,
  Renderer2,
  signal,
  ViewChild,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { debounceTime, distinctUntilChanged, filter } from 'rxjs';
import { MessageService } from 'primeng/api';
import { BadgeModule } from 'primeng/badge';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { CheckboxModule } from 'primeng/checkbox';
import { SidebarModule } from 'primeng/sidebar';
import { SharedModule } from '../../shared/shared.module';

import { customEmailValidator } from '~/shared-common';
import { AuthStore } from '../../stores/auth';
import { OrderDetailStore } from '../../stores/order/order-detail.store';
import { CloudTagListStore } from '../../stores/cloud-tag/cloud-tag-list.store';
import { TopbarService } from '../../stores/services/topbar.service';
import { ProductFrontendService } from '../../stores/products/services/products.service';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-topbar-new',
  templateUrl: './app-topbar.component.html',
  styleUrls: ['./app-topbar.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    SharedModule,
    BadgeModule,
    ButtonModule,
    DialogModule,
    IconFieldModule,
    InputIconModule,
    InputTextModule,
    ToastModule,
    TooltipModule,
    CheckboxModule,
    ReactiveFormsModule,
    SidebarModule,
  ],
  providers: [MessageService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppTopbarNewComponent implements OnInit, OnDestroy {
  private authStore = inject(AuthStore);
  private orderDetailStore = inject(OrderDetailStore);
  private cloudTagListStore = inject(CloudTagListStore);
  private router = inject(Router);
  private fb = inject(FormBuilder);
  private cdr = inject(ChangeDetectorRef);
  private renderer = inject(Renderer2);
  private messageService = inject(MessageService);
  private topbarService = inject(TopbarService);
  private productFrontendService = inject(ProductFrontendService);
  private location = inject(Location);

  private emptySearchTimer: ReturnType<typeof setTimeout> | null = null;
  private lastSubmittedSearch = '';
  isSearchFocused = false;
  private searchBlurTimeout: ReturnType<typeof setTimeout> | null = null;

  onSearchFocus() {
    if (this.searchBlurTimeout) {
      clearTimeout(this.searchBlurTimeout);
      this.searchBlurTimeout = null;
    }
    this.isSearchFocused = true;
    this.clearEmptySearchTimer();
  }

  onSearchBlur() {
    if (this.searchBlurTimeout) {
      clearTimeout(this.searchBlurTimeout);
    }
    this.searchBlurTimeout = setTimeout(() => {
      this.isSearchFocused = false;
    }, 250);
  }

  clearSearch(event?: Event) {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    this.searchForm.get('search')?.setValue('');
    this.clearEmptySearchTimer();
    this.lastSubmittedSearch = '';
    if (this.router.url.includes('/product/search/')) {
      this.productFrontendService.changeFindKey('');
    }
  }

  private clearEmptySearchTimer() {
    if (this.emptySearchTimer) {
      clearTimeout(this.emptySearchTimer);
      this.emptySearchTimer = null;
    }
  }

  private startEmptySearchTimer() {
    this.clearEmptySearchTimer();
    if (this.isSearchFocused) return;
    this.emptySearchTimer = setTimeout(() => {
      if (this.isSearchFocused) return;
      const currentVal = this.searchForm.get('search')?.value;
      const isStillEmpty =
        !currentVal ||
        (typeof currentVal === 'string' && currentVal.trim() === '');
      if (this.router.url.includes('/product/search/') && isStillEmpty) {
        this.router.navigate(['/']);
      }
    }, 10000);
  }

  $currentUser = this.authStore.user;
  $orderDetail = this.orderDetailStore.orderDetail;
  $lastedOrderDetail = this.orderDetailStore.lastedOrderDetail;
  $cloudTags = this.cloudTagListStore.findFullMenuTagForCompany.records;

  imgSrcUrl: string = `${environment.s3_url}/`;
  login_banner: any =
    'upload/products/5e44704b6803faa43cb6e5c3-1739592891282--1739592891282.png';

  // Dialog state
  popupLogin: boolean = false;
  isLoginView: boolean = true;
  popupForgetPassword: boolean = false;
  popupRegister: boolean = false; // Kept for backward compatibility if needed, but we'll use isLoginView
  showPass: boolean = false;
  isLoading: boolean = false;
  errorMessage: string | null = null;
  resetMessage: { text: string; type: 'success' | 'error' } | null = null;
  mobileMenuVisible = signal(false);
  selectedSidebarMenu = signal<any>(null);
  isScrolled = signal(false);
  private lastScrollPos = 0;
  private isAnimating = false;

  // Category bar: horizontal overflow hints + mega menu placement
  @ViewChild('navScroller') navScroller?: ElementRef<HTMLElement>;
  canScrollNavLeft = signal(false);
  canScrollNavRight = signal(false);
  megaMenuPos = signal<{ top: number; left: number } | null>(null);
  private hoveredNavItem: HTMLElement | null = null;
  private hoveredNavAlignRight = false;
  private readonly megaMenuWidth = 650;

  @HostListener('window:resize')
  onWindowResize() {
    this.updateNavScroll();
    this.positionMegaMenu();
  }

  onNavScroll() {
    this.updateNavScroll();
    this.positionMegaMenu();
  }

  updateNavScroll() {
    const el = this.navScroller?.nativeElement;
    if (!el) return;
    this.canScrollNavLeft.set(el.scrollLeft > 1);
    this.canScrollNavRight.set(
      el.scrollLeft + el.clientWidth < el.scrollWidth - 1,
    );
  }

  scrollNav(direction: 1 | -1) {
    const el = this.navScroller?.nativeElement;
    if (!el) return;
    el.scrollBy({ left: direction * el.clientWidth * 0.6, behavior: 'smooth' });
  }

  // The menu list scrolls horizontally, which would clip an absolutely
  // positioned dropdown, so the mega menu is fixed and placed from the
  // hovered item's on-screen position.
  private positionMegaMenu() {
    const item = this.hoveredNavItem;
    if (!item) return;
    const rect = item.getBoundingClientRect();
    const preferred = this.hoveredNavAlignRight
      ? rect.right - this.megaMenuWidth
      : rect.left;
    const maxLeft = window.innerWidth - this.megaMenuWidth - 16;
    this.megaMenuPos.set({
      top: rect.bottom,
      left: Math.max(16, Math.min(preferred, maxLeft)),
    });
  }

  @HostListener('window:scroll', [])
  onWindowScroll() {
    this.positionMegaMenu();
    if (this.isAnimating) return;
    if (this.isSearchFocused) return;

    const scrollPos =
      window.pageYOffset ||
      document.documentElement.scrollTop ||
      document.body.scrollTop ||
      0;

    // Hysteresis: Use different thresholds for showing vs hiding
    const showThreshold = 10;
    const hideThreshold = 60;

    if (scrollPos < showThreshold) {
      if (this.isScrolled()) {
        this.toggleSearchVisibility(false);
      }
    } else if (scrollPos > hideThreshold) {
      if (!this.isScrolled()) {
        this.toggleSearchVisibility(true);
      }
    }
  }

  private toggleSearchVisibility(scrolled: boolean) {
    this.isScrolled.set(scrolled);
    this.isAnimating = true;
    setTimeout(() => {
      this.isAnimating = false;
    }, 350); // Matches CSS transition (300ms) + small buffer
  }

  selectSidebarMenu(menu: any) {
    if (menu.childs && menu.childs.length > 0) {
      this.selectedSidebarMenu.set(menu);
    } else {
      this.router.navigate(['/category', menu.alias]);
      this.closeMobileMenu();
    }
  }

  goBackSidebar() {
    this.selectedSidebarMenu.set(null);
  }

  searchForm = this.fb.group({
    search: [''],
  });

  loginFormGroup = this.fb.group({
    username: ['', [Validators.required, Validators.maxLength(255)]],
    password: ['', [Validators.required, Validators.minLength(3)]],
  });

  forgetEmailFormGroup = this.fb.group({
    forget_email: [
      '',
      [
        Validators.required,
        Validators.email,
        customEmailValidator(),
        Validators.maxLength(255),
      ],
    ],
  });

  registerFormGroup = this.fb.group({
    user_email: [
      '',
      [
        Validators.required,
        Validators.email,
        customEmailValidator(),
        Validators.maxLength(255),
      ],
    ],
    user_password: ['', [Validators.required, Validators.maxLength(30)]],
    user_first_name: ['', [Validators.required, Validators.maxLength(30)]],
    user_last_name: ['', [Validators.required, Validators.maxLength(30)]],
    user_phone: ['', [Validators.required, Validators.minLength(3)]],
    company_label: [''],
    terms: [false, Validators.requiredTrue],
  });

  constructor() {
    // Re-measure the category bar once the menu items have rendered
    effect(() => {
      this.$menusItems();
      setTimeout(() => this.updateNavScroll());
    });
    effect(
      () => {
        this.init();
        // Auto-close popup when user logs in (no longer a guest)
        if (!this.$currentUser()?.default_user && this.popupLogin) {
          this.popupLogin = false;
          this.onDialogHide();
          this.cdr.detectChanges();
        }
      },
      {
        allowSignalWrites: true,
      },
    );
  }

  init() {
    // Load login banner from company config
    if (
      this.$currentUser() &&
      this.$currentUser()?.company &&
      this.$currentUser()?.company?.login_banner
    ) {
      this.login_banner = this.$currentUser()?.company?.login_banner;
    }

    if (
      this.$currentUser()?._id &&
      !this.cloudTagListStore.findFullMenuTagForCompany.totalCount()
    ) {
      const params: any = [
        this.$currentUser()?.company_id,
        this.$currentUser()?.company?.use_company_data ?? 0,
        this.$currentUser(),
      ];
      this.cloudTagListStore.getMenuData(params);
      this.cloudTagListStore.getFolderMenuData(params);
    }
  }

  hoveredMenu = signal<any>(null);

  $cartCount = computed(() => {
    return this.$orderDetail()?.order_item_list?.length || 0;
  });

  $formattedOrderId = computed(() => {
    const order = this.$orderDetail();
    if (!order || !order.order_id) return '';
    const logincode = this.$currentUser()?.company?.logincode || '';
    let tmp: string = logincode ? logincode + '-' : '';
    tmp += String(order.order_id).padStart(4, '0');
    return tmp;
  });

  $menusItems = computed(() => {
    const records = this.$cloudTags();
    return records.filter((item: any) => item.status === 1);
  });

  ngOnInit(): void {
    this.topbarService.registerCalled$.subscribe(() => this.callRegister());
    this.topbarService.signedInCallSource$.subscribe(() => this.callLogin());
    this.topbarService.forgotPasswordCalled$.subscribe(() =>
      this.forgotPassword(),
    );

    // Live search as user types
    this.searchForm
      .get('search')
      ?.valueChanges.pipe(debounceTime(400), distinctUntilChanged())
      .subscribe((searchTerm) => {
        const trimmed = typeof searchTerm === 'string' ? searchTerm.trim() : '';
        if (trimmed.length >= 2) {
          this.clearEmptySearchTimer();
          this.onSearch();
        } else if (trimmed.length === 0) {
          if (this.router.url.includes('/product/search/')) {
            this.startEmptySearchTimer();
          }
        } else {
          // 1 character typed: user is actively typing, cancel any pending redirect
          this.clearEmptySearchTimer();
        }
      });

    // Sync search bar with URL and clear on navigation away
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: any) => {
        const url = event.urlAfterRedirects || event.url;

        // If the user is actively typing in the search input, do not overwrite or clear it
        if (this.isSearchFocused) {
          this.onDialogHide();
          return;
        }

        // Check if we're on the search results page
        if (url.includes('/product/search/')) {
          const parts = url.split('/product/search/');
          if (parts.length > 1) {
            const rawTerm = parts[1].split('?')[0].split('#')[0];
            const term = decodeURIComponent(rawTerm);
            // Only update if the value is actually different to avoid redundant updates
            const currentVal = this.searchForm.get('search')?.value;
            if (currentVal !== term) {
              this.searchForm
                .get('search')
                ?.setValue(term, { emitEvent: false });
            }
          }
        } else {
          // Clear search when navigating to any other page
          this.clearEmptySearchTimer();
          this.lastSubmittedSearch = '';
          const currentVal = this.searchForm.get('search')?.value;
          if (currentVal !== '') {
            this.searchForm.get('search')?.setValue('', { emitEvent: false });
          }
        }
        // Ensure scrollbar is restored on any navigation
        this.onDialogHide();
      });
  }

  ngOnDestroy(): void {
    this.clearEmptySearchTimer();
    if (this.searchBlurTimeout) {
      clearTimeout(this.searchBlurTimeout);
      this.searchBlurTimeout = null;
    }
  }

  onSearch() {
    this.clearEmptySearchTimer();
    const rawSearchTerm = this.searchForm.get('search')?.value;
    const searchTerm =
      typeof rawSearchTerm === 'string' ? rawSearchTerm.trim() : '';
    if (searchTerm) {
      const isAlreadyOnSearch = this.router.url.includes('/product/search/');
      if (isAlreadyOnSearch && this.lastSubmittedSearch === searchTerm) {
        return;
      }
      this.lastSubmittedSearch = searchTerm;
      this.productFrontendService.changeFindKey(searchTerm);

      if (isAlreadyOnSearch) {
        // In-place URL update: avoids full Angular route recreation, preventing DOM jumps and input rollback
        this.location.replaceState(
          `/product/search/${encodeURIComponent(searchTerm)}`,
        );
      } else {
        this.router.navigateByUrl(
          `/product/search/${encodeURIComponent(searchTerm)}`,
        );
      }
    }
  }

  displayImage(image_file: any) {
    if (!image_file || image_file == '') return '';
    if (image_file[0] == '/') {
      image_file = image_file.substring(1);
    }
    return this.imgSrcUrl + image_file;
  }

  splitIntoColumns(items: any[], cols = 3): any[][] {
    const result: any[][] = Array.from({ length: cols }, () => []);
    items.forEach((item, i) => result[i % cols].push(item));
    return result;
  }

  setHoveredMenu(menu: any, event?: MouseEvent, alignRight = false) {
    this.hoveredNavItem = (event?.currentTarget as HTMLElement) ?? null;
    this.hoveredNavAlignRight = alignRight;
    this.positionMegaMenu();
    this.hoveredMenu.set(menu);
  }

  clearHoveredMenu() {
    this.hoveredNavItem = null;
    this.hoveredMenu.set(null);
  }

  toggleMobileMenu() {
    this.mobileMenuVisible.update((v) => !v);
  }

  closeMobileMenu() {
    this.mobileMenuVisible.set(false);
    this.selectedSidebarMenu.set(null);
    this.isSearchFocused = false;
  }

  // --- Login / Register / Forgot Password ---

  callLogin() {
    this.popupLogin = true;
    this.isLoginView = true;
    this.popupRegister = false;
    this.popupForgetPassword = false;
    this.cdr.markForCheck();
  }

  callRegister() {
    if (this.$currentUser()?.company_id == '10026') {
      this.popupLogin = false;
      this.popupRegister = false;
      this.isLoginView = false;
      this.popupForgetPassword = false;
      this.router.navigateByUrl('resources/register');
    } else {
      this.popupLogin = true; // Use the same dialog
      this.isLoginView = false;
      this.popupRegister = false;
      this.popupForgetPassword = false;
    }
    this.cdr.markForCheck();
  }

  forgotPassword() {
    this.popupRegister = false;
    this.popupLogin = false;
    this.popupForgetPassword = true;
    this.resetMessage = null;
    this.cdr.markForCheck();
  }

  onDialogHide() {
    // Aggressive cleanup for body and html
    const elements = [document.body, document.documentElement];
    elements.forEach((el) => {
      this.renderer.removeClass(el, 'p-overflow-hidden');
      this.renderer.setStyle(el, 'overflow', '');
      this.renderer.setStyle(el, 'padding-right', '');
    });

    // Fallback for bootstrap modal classes if they exist
    this.renderer.removeClass(document.body, 'modal-open');

    // Double check after a tick to ensure PrimeNG didn't re-add it
    setTimeout(() => {
      elements.forEach((el) => {
        el.style.overflow = '';
        el.classList.remove('p-overflow-hidden');
      });
    }, 100);
  }

  login() {
    let { username, password } = this.loginFormGroup.value;
    username = username?.trim().toLowerCase();
    this.isLoading = true;
    this.authStore.login({
      data: { username, password, direct: 0 },
    });
    // The effect() will auto-close popup on successful login
    setTimeout(() => {
      this.isLoading = false;
      this.cdr.detectChanges();
    }, 2000);
  }

  resetPassword() {
    let { forget_email } = this.forgetEmailFormGroup.value;
    forget_email = forget_email?.trim().toLowerCase();
    this.isLoading = true;
    this.resetMessage = null;
    this.cdr.markForCheck();
    this.authStore
      .forgetPassword({ user_email: forget_email } as any)
      .subscribe({
        next: (result: any) => {
          this.isLoading = false;
          if (result) {
            this.resetMessage = {
              text: 'A password reset link has been sent to your email address.',
              type: 'success',
            };
          } else {
            this.resetMessage = {
              text: 'This email address is not registered. Please check and try again.',
              type: 'error',
            };
          }
          this.cdr.markForCheck();
        },
        error: (error: any) => {
          this.isLoading = false;
          this.resetMessage = {
            text: 'Something went wrong. Please try again later.',
            type: 'error',
          };
          this.cdr.markForCheck();
        },
      });
  }

  registerUser() {
    this.isLoading = true;
    this.authStore
      .registerUser([
        {
          user_email: this.registerFormGroup.value.user_email,
          user_password: this.registerFormGroup.value.user_password,
          user_first_name: this.registerFormGroup.value.user_first_name,
          user_last_name: this.registerFormGroup.value.user_last_name,
          user_phone: this.registerFormGroup.value.user_phone,
          company_label: this.registerFormGroup.value.company_label,
        },
      ])
      .subscribe({
        next: (result: any) => {
          if (result._id === '') {
            this.errorMessage =
              'This email is already registered, please try a different email.';
            this.isLoading = false;
            this.cdr.markForCheck();
          } else {
            if (result.user_status) {
              this.messageService.add({
                severity: 'info',
                summary: 'Confirmed',
                detail: `Welcome ${this.registerFormGroup.value.user_email}`,
              });
              this.performLoginAfterRegister(result);
            } else {
              this.popupRegister = false;
              this.popupLogin = false;
              this.isLoading = false;
              this.cdr.markForCheck();
            }
          }
        },
        error: (error: any) => {
          this.isLoading = false;
          this.cdr.markForCheck();
        },
      });
  }

  private performLoginAfterRegister(result: any) {
    const data: any = {
      username: this.registerFormGroup.value.user_email,
      password: this.registerFormGroup.value.user_password,
      direct: 0,
    };
    this.authStore.login({ data });
    this.popupLogin = false;
    this.onDialogHide();
    this.isLoading = false;
    this.cdr.markForCheck();
  }

  clearErrorMessage() {
    this.errorMessage = null;
  }

  logout() {
    this.authStore.setLoggedOut({}, true);
  }
}
